<template>
  <figure v-if="imageUrl" class="my-6" :data-blok-c="dataBlokC" :data-blok-uid="dataBlokUid">
    <!-- Video source (e.g. an S3/asset .mp4): native player shows the first
         frame as its poster (no CORS canvas needed). -->
    <video
      v-if="isVideo"
      :src="imageUrl"
      class="w-full rounded-[10px] bg-black object-contain"
      :class="imageClasses"
      preload="metadata"
      controls
      controlsList="nodownload nofullscreen noremoteplayback"
      playsinline
      disablePictureInPicture
      @contextmenu.prevent
    ></video>
    <img
      v-else
      :src="optimizedUrl"
      :alt="altText || 'Image'"
      class="w-full rounded-[10px] object-cover"
      :class="imageClasses"
      loading="lazy"
      @error="handleImageError"
    />
    <figcaption
      v-if="caption"
      class="mt-[4px] text-[0.75rem] leading-[1rem] text-[#353535] text-center tracking-[-0.12px]"
    >
      {{ caption }}
    </figcaption>
  </figure>
</template>

<script>
import { optimizeStoryblokImage, getRetryUrl } from '@/utils/storyblok-image';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];

export default {
  name: 'ContentImage',
  props: {
    imageUrl: {
      type: String,
      default: '',
    },
    altText: {
      type: String,
      default: '',
    },
    caption: {
      type: String,
      default: '',
    },
    aspectRatio: {
      type: String,
      default: 'auto',
      validator: value => ['auto', 'square', 'wide', 'tall'].includes(value),
    },
    dataBlokC: {
      type: String,
      default: '',
    },
    dataBlokUid: {
      type: String,
      default: '',
    },
  },
  data() {
    return { retryCount: 0 };
  },
  computed: {
    isVideo() {
      const url = (this.imageUrl || '').toLowerCase();
      return VIDEO_EXTENSIONS.some(
        ext => url.endsWith(ext) || url.includes(`${ext}?`) || url.includes(`${ext}#`),
      );
    },
    optimizedUrl() {
      return optimizeStoryblokImage(this.imageUrl, 800);
    },
    imageClasses() {
      const aspectMap = {
        auto: 'aspect-auto',
        square: 'aspect-square',
        wide: 'aspect-video',
        tall: 'aspect-[3/4]',
      };
      return aspectMap[this.aspectRatio] || 'aspect-auto';
    },
  },
  methods: {
    handleImageError(event) {
      this.retryCount++;
      const retry = getRetryUrl(this.imageUrl, this.retryCount);
      if (retry) {
        setTimeout(() => {
          event.target.src = optimizeStoryblokImage(retry.url, 800);
        }, retry.delay);
      }
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Inter', 'aktiv-grotesk', sans-serif;
}
</style>
